import { DiscordSDK } from '@discord/embedded-app-sdk';
import { APIManager } from './managers';
import type { DiscordParticipant } from './types';

declare const __DISCORD_CLIENT_ID__: string;
const DISCORD_CLIENT_ID = __DISCORD_CLIENT_ID__ || '';

// Only create SDK if we have a client ID and are in Discord iframe
const isInDiscord = window.location.search.includes('frame_id');
export const discordSdk = DISCORD_CLIENT_ID && isInDiscord
  ? new DiscordSDK(DISCORD_CLIENT_ID)
  : null;

// Apply Discord Activity top-bar inset on mobile only (Discord's chrome is taller there)
const discordPlatform = new URLSearchParams(window.location.search).get('platform');
export const isDiscordMobile = isInDiscord && discordPlatform === 'mobile';
if (isDiscordMobile) {
  document.documentElement.classList.add('discord-mobile');
  document.documentElement.style.setProperty('--discord-inset-top', '50px');
}

export interface DiscordUser {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
}

export async function setupDiscord(): Promise<DiscordUser | null> {
  if (!discordSdk) {
    console.log('[Discord] SDK not available (dev mode)');
    return null;
  }

  try {
    // Wait for SDK to be ready
    await discordSdk.ready();
    console.log('[Discord] SDK ready');

    // Authorize with Discord
    const { code } = await discordSdk.commands.authorize({
      client_id: DISCORD_CLIENT_ID,
      response_type: 'code',
      state: '',
      prompt: 'none',
      scope: ['identify', 'rpc.activities.write'],
    });
    console.log('[Discord] Authorization code received');

    // Exchange code for access token via backend
    const tokenResult = await APIManager.exchangeToken(code);
    if (!tokenResult.success || !tokenResult.data) {
      console.error('[Discord] Token exchange failed:', tokenResult.error);
      return null;
    }

    const { access_token } = tokenResult.data;
    APIManager.setAccessToken(access_token);

    // Authenticate with Discord
    await discordSdk.commands.authenticate({ access_token });
    console.log('[Discord] Authenticated');

    // Return user info (user is available after authenticate)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sdk = discordSdk as any;
    if (sdk.user) {
      return {
        id: sdk.user.id,
        username: sdk.user.username,
        discriminator: sdk.user.discriminator ?? '0',
        avatar: sdk.user.avatar ?? null,
      };
    }

    return null;
  } catch (error) {
    console.error('[Discord] Setup failed:', error);
    return null;
  }
}

export function getGuildId(): string | null {
  return discordSdk?.guildId ?? null;
}

export function getChannelId(): string | null {
  return discordSdk?.channelId ?? null;
}

interface DesktopBridge {
  setPresence?: (details: string, state?: string | null) => void;
  clearPresence?: () => void;
  /** Steam: unlock an achievement by API name. No-op when Steam SDK isn't loaded. */
  unlockAchievement?: (steamApiName: string) => void;
  /** Steam: set an integer stat (used for achievement progress bars). */
  setSteamStat?: (steamStatName: string, value: number) => void;
}

export function getDesktopBridge(): DesktopBridge | null {
  return (window as unknown as { fatewovenDesktop?: DesktopBridge }).fatewovenDesktop ?? null;
}

/**
 * Update the player's Rich Presence on whichever platform is active.
 * Fans out to Discord (when running in the Activity iframe) and Steam (when
 * running in the Electron+Steam shell) — the data source is the same.
 */
export async function setPresence(details: string, state?: string): Promise<void> {
  getDesktopBridge()?.setPresence?.(details, state ?? null);

  if (!discordSdk) return;
  try {
    await discordSdk.commands.setActivity({
      activity: {
        type: 0, // Playing
        details,
        state: state ?? undefined,
        timestamps: { start: activityStartTime },
      },
    });
  } catch (error) {
    console.warn('[Discord] Failed to set activity:', error);
  }
}

/** Clear the player's Rich Presence on exit (Discord + Steam). */
export async function clearPresence(): Promise<void> {
  getDesktopBridge()?.clearPresence?.();

  if (!discordSdk) return;
  try {
    await discordSdk.commands.setActivity({ activity: null });
  } catch {
    // Ignore — activity may already be cleared
  }
}

const activityStartTime = Math.floor(Date.now() / 1000);

// --- Social Presence ---

/** Get all users currently in this Activity instance. */
export async function getParticipants(): Promise<DiscordParticipant[]> {
  if (!discordSdk) return [];
  try {
    const result = await discordSdk.commands.getInstanceConnectedParticipants();
    return result.participants.map(p => ({
      id: p.id,
      username: p.username,
      avatar: p.avatar ?? null,
      global_name: p.global_name ?? null,
      nickname: p.nickname,
    }));
  } catch (error) {
    console.warn('[Discord] Failed to get participants:', error);
    return [];
  }
}

/** Subscribe to real-time participant join/leave events. Returns unsubscribe fn. */
export function subscribeParticipants(
  callback: (participants: DiscordParticipant[]) => void
): () => void {
  if (!discordSdk) return () => {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handler = (data: any) => {
    const mapped: DiscordParticipant[] = (data.participants ?? []).map((p: any) => ({
      id: p.id,
      username: p.username,
      avatar: p.avatar ?? null,
      global_name: p.global_name ?? null,
      nickname: p.nickname,
    }));
    callback(mapped);
  };

  discordSdk.subscribe('ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE', handler);
  return () => {
    discordSdk?.unsubscribe('ACTIVITY_INSTANCE_PARTICIPANTS_UPDATE', handler);
  };
}

/** Build a Discord CDN avatar URL, or a default avatar if no hash. */
export function discordAvatarUrl(userId: string, avatarHash: string | null, size = 64): string {
  if (avatarHash) {
    return `https://cdn.discordapp.com/avatars/${userId}/${avatarHash}.webp?size=${size}`;
  }
  // Default avatar index — real Discord IDs are numeric snowflakes, mock IDs may not be
  let index = 0;
  try {
    index = Number(BigInt(userId) >> 22n) % 6;
  } catch {
    // Non-numeric ID (dev mode) — hash to a stable index
    let hash = 0;
    for (let i = 0; i < userId.length; i++) hash = (hash * 31 + userId.charCodeAt(i)) | 0;
    index = Math.abs(hash) % 6;
  }
  return `https://cdn.discordapp.com/embed/avatars/${index}.png`;
}
